import t from '@niaratech/niara-js-commons/dist/t'
import type { Field } from './fillCrudMetadataGaps'

interface GraphQLOperation {
  name: string
  queryVariables
  queryArguments
}

export type CrudMetadata<T = any> = {
  authorization?: { read: Array<string>; write?: Array<string> }
  fields: Field[]
  graphQL: {
    findById?: GraphQLOperation
    search?: GraphQLOperation
    delete?: GraphQLOperation
    update?: GraphQLOperation
    create?: GraphQLOperation
  }
  columns?: Array<string | Field>
}

type FieldParameter =
  | string
  | Field
  | {
      name: string
      fields?: Array<FieldParameter>
    }

export interface SearchResult<T = any> {
  items: T[]
  nextToken?: any
}

export type Filter = Record<string, unknown>

type OtherParams = Partial<Record<string, unknown>>

type AbortablePromise<T> = Promise<T> & { abort?: () => Promise<T> }

export interface DAO<
  T = any,
  C = OtherParams,
  U = OtherParams,
  L = OtherParams,
  D = OtherParams,
  F extends Filter = Filter
> {
  findById(id): Promise<T>
  load(instanceAndParams: Partial<T> & L, params?: { fields?: FieldParameter[] }): Promise<T>
  search(params?: { fields?: FieldParameter[]; filter?: F; nextToken?: any; limit?: number }): Promise<SearchResult<T>>
  searchAll(
    params?: { fields?: FieldParameter[]; filter?: F; maxPages?: number; nextToken?: string; limit?: number },
    eventListener?: SearchAllEventListener
  ): AbortablePromise<{ items: T[]; nextToken?: string }>
  create?(instanceAndParams: Partial<T> & C, params?: { fields?: FieldParameter[] }): Promise<T>
  update?(instanceAndParams: Partial<T> & U, params?: { fields?: FieldParameter[] }): Promise<T>
  delete?(instanceAndParams: Partial<T> & D, params?: { fields?: FieldParameter[] }): Promise<T>
}

export interface GraphQLResult<T> {
  data?: T
  errors?: { message?: string; errorType: string }[]
  extensions?: {
    [key: string]: any
  }
}

export interface GraphQLFunction<T> {
  (query: any, variables?): Promise<GraphQLResult<T>>
}

interface SearchAllEventListener {
  emit: { (type: 'response' | 'partial' | 'done', event) }
}

function addField(f: FieldParameter) {
  if (typeof f === 'string') {
    return f
  } else if (f.fields && f.fields.length > 0) {
    const fields: Array<FieldParameter> = f.fields
    return t`
      ${f.name} {
        ${fields.map(addField).join('\n')}
      }
    `
  } else {
    return f.name
  }
}

export class GraphqlResultError extends Error {
  errorType: string
  constructor(errorType: string, ...params) {
    super(...params)
    this.errorType = errorType
  }
}

const prepareResponse = function <T>(fields: Field<any, T>[], entity: T): T {
  if (!entity) return entity
  if (fields) {
    fields.forEach((field) => {
      const v = entity[field.name]
      if (field.graphQLType == 'AWSJSON') {
        if (v && typeof v === 'string') {
          entity[field.name] = JSON.parse(v)
        }
      } else if (field.type == 'date') {
        if (v && typeof v === 'string' && v.length > 10) {
          entity[field.name] = v.substring(0, 10) as any
        }
      }
      if (field.type == 'object' && field.fields?.length > 0) {
        entity[field.name] = prepareResponse(field.fields, entity[field.name])
      }
      if (field.type == 'objectarray' && field.fields?.length > 0 && v && Array.isArray(v)) {
        if (v?.length > 0) {
          v.forEach((i) => prepareResponse(field.fields, i))
        }
      }
    })
  }
  return entity
}

class GraphQLDAO<T = any, C = OtherParams, U = OtherParams, L = OtherParams, D = OtherParams, F extends Filter = Filter>
  implements DAO<T, C, U, L, D, F>
{
  public static defaultExecuteQuery: GraphQLFunction<any>
  crudMetadata: CrudMetadata<T>
  readRole: any
  writeRole: any
  private _executeQuery?: GraphQLFunction<T>
  prepareResponse: (entity: T) => T

  constructor(props: { crudMetadata: CrudMetadata<T>; executeQuery?: GraphQLFunction<T> }) {
    this._executeQuery = props.executeQuery
    this.setCrudMetadata(props.crudMetadata)
  }

  graphqlFunction = (): GraphQLFunction<T> => {
    const f: GraphQLFunction<T> = this._executeQuery || GraphQLDAO.defaultExecuteQuery
    if (!f) {
      throw new Error('GraphQLDAO não está corretamente configurado')
    }
    return f
  }

  setCrudMetadata = (crudMetadata: CrudMetadata<T>): void => {
    this.crudMetadata = crudMetadata
    this.readRole =
      (this.crudMetadata.authorization &&
        this.crudMetadata.authorization.read &&
        this.crudMetadata.authorization.read[0]) ||
      'consultant'
    this.writeRole =
      (this.crudMetadata.authorization &&
        this.crudMetadata.authorization.write &&
        this.crudMetadata.authorization.write[0]) ||
      'consultant'
    this.prepareResponse = prepareResponse.bind(this, crudMetadata.fields)
  }

  checkErrors = (errors: GraphQLResult<T>['errors']): void => {
    if (errors?.length > 0) {
      throw new GraphqlResultError(errors[0]['errorType'], errors.map((e) => e.message).join(', '))
    }
  }

  /**
   * Funciona apenas se a entidade tiver uma chave primária chamada `id`.
   * `dao.findById(id) == dao.load({id})`
   */
  findById = async (id): Promise<T> => {
    return await this.load({ id } as unknown as Partial<T>)
  }

  /**
   * Aceita entidades com chave composta. Dar preferência a este ao invés de `findById`.
   * @param {Object} instanceWithId - Objeto com o id desejado
   */
  load = async (instanceWithId: Partial<T> = {}, params: { fields?: FieldParameter[] } = {}): Promise<T> => {
    const role = this.readRole
    const fields = params.fields || this.crudMetadata.fields.filter((r) => !r.deprecated && r.fetchByDefault)
    const { queryVariables, name, queryArguments } = this.crudMetadata.graphQL.findById
    const Query = t`
      query Q${queryVariables && `(${queryVariables})`} {
        ${role} {
          ${name}${queryArguments && `(${queryArguments})`} {
            ${fields.map(addField).join('\n')}
          }
        }
      }
    `

    const { data, errors } = await this.graphqlFunction()(Query, instanceWithId)
    const response = data?.[role]?.[name]
    if (!response) {
      this.checkErrors(errors)
    }
    return response ? this.prepareResponse(response) : response
  }

  search = async (
    params: { fields?: FieldParameter[]; filter?: F; nextToken?: any; limit?: number } = {}
  ): Promise<SearchResult<T>> => {
    const role = this.readRole
    const { filter, nextToken, limit } = params
    const fields: FieldParameter[] = params.fields || this.crudMetadata.columns
    const searchParams = Object.assign({ nextToken, limit }, filter || {})
    const { queryVariables, name, queryArguments } = this.crudMetadata.graphQL.search
    const SearchQuery = t`
      query Search(${queryVariables}) {
        ${role} {
          ${name}(${queryArguments}) {
            items {
              ${fields.map(addField).join('\n')}
            }
            nextToken
          }
        }
      }
    `

    const { data, errors } = await this.graphqlFunction()(SearchQuery, searchParams)
    const response = data?.[role]?.[name]
    if (!response) {
      this.checkErrors(errors)
    }
    if (response?.items) response.items.forEach((e) => this.prepareResponse(e))
    return response || { items: [] }
  }

  searchAll = (
    params: { fields?: FieldParameter[]; filter?: F; maxPages?: number; nextToken?: string; limit?: number } = {},
    eventListener?: SearchAllEventListener
  ): AbortablePromise<{ items: T[]; nextToken?: string }> => {
    let interrupted = false

    const implementationFn = async () => {
      let nextToken = params?.nextToken || undefined
      const items: T[] = []
      const maxPagesLimit = params?.maxPages ?? 50
      let pages = maxPagesLimit
      let limit = params?.limit
      do {
        pages--
        const response = await this.search({ ...params, nextToken, limit })
        nextToken = response.nextToken
        items.splice(items.length, 0, ...response.items)

        if (limit !== undefined) {
          limit -= response?.items?.length
        }

        if (eventListener) {
          eventListener.emit('response', response)
          eventListener.emit('partial', items)
        }
      } while (nextToken && pages > 0 && !interrupted && (limit === undefined || items.length < limit))

      if (eventListener) {
        eventListener.emit('done', items)
      }

      return {
        items,
        nextToken,
      }
    }

    const promise = new Promise<{ items: T[]; nextToken?: string }>((resolve, reject) => {
      implementationFn().then(resolve).catch(reject)
    })
    const abort: AbortablePromise<{ items: T[]; nextToken?: string }>['abort'] = () => {
      interrupted = true
      return promise
    }
    promise['abort'] = abort

    return promise
  }

  update = async (entity: Partial<T> & OtherParams, params: { fields?: FieldParameter[] } = {}): Promise<T> => {
    const role = this.writeRole
    const fields = params.fields || this.crudMetadata.fields
    const { queryVariables, name, queryArguments } = this.crudMetadata.graphQL.update

    const Mutation = t`
      mutation (${queryVariables}) {
        ${role} {
          ${name}(${queryArguments}) {
            ${fields.map(addField).join('\n')}
          }
        }
      }
    `

    const { data, errors } = await this.graphqlFunction()(Mutation, { ...entity })
    const response = data?.[role]?.[name]
    if (!response) {
      this.checkErrors(errors)
    }
    return this.prepareResponse(response)
  }

  create = async (entity: Partial<T> & OtherParams, params: { fields?: FieldParameter[] } = {}): Promise<T> => {
    const role = this.writeRole
    const fields = params.fields || this.crudMetadata.fields
    const { queryVariables, name, queryArguments } = this.crudMetadata.graphQL.create

    const Mutation = t`
      mutation (${queryVariables}) {
        ${role} {
          ${name}(${queryArguments}) {
            ${fields.map(addField).join('\n')}
          }
        }
      }
    `

    const { data, errors } = await this.graphqlFunction()(Mutation, { ...entity })
    const response = data?.[role]?.[name]
    if (!response) {
      this.checkErrors(errors)
    }
    return this.prepareResponse(response)
  }

  /**
   * @param {Object} entity - Objeto com o id
   */
  delete = async (entity: Partial<T>, params: { fields?: FieldParameter[] } = {}): Promise<T> => {
    const role = this.writeRole
    const fields = params.fields || this.crudMetadata.fields
    const { queryVariables, name, queryArguments } = this.crudMetadata.graphQL.delete

    const Mutation = t`
      mutation (${queryVariables}) {
        ${role} {
          ${name}(${queryArguments}) {
            ${fields.map(addField).join('\n')}
          }
        }
      }
    `

    const { data, errors } = await this.graphqlFunction()(Mutation, { ...entity })
    const response = data?.[role]?.[name]
    if (!response) {
      this.checkErrors(errors)
    }
    return this.prepareResponse(response)
  }
}

export default GraphQLDAO
